// Retail barcodes for product labels: UPC-A (12 digits), EAN-13 and EAN-8, as the bars a label draws (no dependency).
// A product's barcode (item.barcode, from Shopify) is a GTIN; gtinOf() says which of these it is, or why it isn't one.
// The module patterns are GS1's: each digit is 7 modules, odd parity (L), even (G) or right-hand (R).

const L = ["0001101", "0011001", "0010011", "0111101", "0100011", "0110001", "0101111", "0111011", "0110111", "0001011"];
const R = L.map((p) => [...p].map((b) => (b === "1" ? "0" : "1")).join(""));
const G = R.map((p) => [...p].reverse().join(""));
/** EAN-13's first digit sets which of the left six digits are G (the rest L). UPC-A is EAN-13 with a leading 0. */
const PARITY = ["LLLLLL", "LLGLGG", "LLGGLG", "LLGGGL", "LGLLGG", "LGGLLG", "LGGGLL", "LGLGLG", "LGLGGL", "LGGLGL"];

export type GtinKind = "upca" | "ean13" | "ean8";
export type Gtin = { kind: GtinKind; digits: string };

/** The GS1 check digit for these digits (all but the check digit itself). */
export function checkDigit(body: string): number {
  let sum = 0;
  for (let i = 0; i < body.length; i++) sum += Number(body[body.length - 1 - i]) * (i % 2 === 0 ? 3 : 1);
  return (10 - (sum % 10)) % 10;
}

const valid = (digits: string) => checkDigit(digits.slice(0, -1)) === Number(digits.at(-1));

/** A UPC-E (8 digits, number system 0 or 1) as the UPC-A it stands for, or null when it isn't one. */
export function upcEToA(upce: string): string | null {
  if (!/^[01]\d{7}$/.test(upce)) return null;
  const [ns, d1, d2, d3, d4, d5, d6, check] = [...upce];
  const body =
    d6 <= "2"
      ? `${d1}${d2}${d6}0000${d3}${d4}${d5}`
      : d6 === "3"
        ? `${d1}${d2}${d3}00000${d4}${d5}`
        : d6 === "4"
          ? `${d1}${d2}${d3}${d4}00000${d5}`
          : `${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  const upca = `${ns}${body}${check}`;
  return valid(upca) ? upca : null;
}

/**
 * A barcode as a GTIN a retail scanner reads (spaces and dashes ignored), or why it isn't one. A GTIN-14 or EAN-13
 * padded with leading zeros is the UPC-A (or EAN-13) it pads; a UPC-E is printed as the UPC-A it stands for (the same
 * number, which every scanner reads), unless the 8 digits are a valid EAN-8 too.
 */
export function gtinOf(barcode: string): Gtin | { error: string } {
  let digits = barcode.replace(/[\s-]/g, "");
  if (!/^\d+$/.test(digits)) return { error: "it isn't all digits" };
  if (digits.length === 14 && digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length === 13 && digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length === 8 && !valid(digits)) {
    const upca = upcEToA(digits);
    if (upca) return { kind: "upca", digits: upca };
  }
  const kind: GtinKind | undefined = ({ 12: "upca", 13: "ean13", 8: "ean8" } as Record<number, GtinKind>)[digits.length];
  if (!kind) return { error: `${digits.length} digits isn't a UPC (12) or EAN (13 or 8)` };
  if (!valid(digits)) return { error: "its check digit is wrong" };
  return { kind, digits };
}

/** How many modules wide a GTIN's symbol is, its quiet zones included (UPC-A and EAN-13 113, EAN-8 81). */
export const gtinWidthModules = (g: Gtin) => (g.kind === "ean8" ? 67 + 14 : 95 + 18);

/**
 * The bars of a GTIN, one character per module ("1" bar, "0" space), with which modules are guard bars (they run a
 * little longer, under the digits). 95 modules for UPC-A and EAN-13, 67 for EAN-8; quiet zones aren't included.
 */
export function gtinModules(g: Gtin): { modules: string; guard: boolean[] } {
  const parts: [string, boolean][] = [];
  const left = (d: string, set: string) => parts.push([(set === "G" ? G : L)[Number(d)], false]);
  const right = (d: string) => parts.push([R[Number(d)], false]);
  const ean13 = g.kind === "upca" ? `0${g.digits}` : g.digits;
  parts.push(["101", true]);
  if (g.kind === "ean8") {
    for (const d of g.digits.slice(0, 4)) left(d, "L");
    parts.push(["01010", true]);
    for (const d of g.digits.slice(4)) right(d);
  } else {
    const parity = PARITY[Number(ean13[0])];
    [...ean13.slice(1, 7)].forEach((d, i) => {
      left(d, parity[i]);
    });
    parts.push(["01010", true]);
    for (const d of ean13.slice(7)) right(d);
  }
  parts.push(["101", true]);
  return { modules: parts.map(([m]) => m).join(""), guard: parts.flatMap(([m, isGuard]) => [...m].map(() => isGuard)) };
}
